/**
 * External Link Builder Registry
 *
 * Manifests can annotate a parameter with an external-link reference via
 * `x-ms-editor-options.externalLink.builderId`. The registry maps a builder
 * id to a function that produces the deep-link URL from the current parameter
 * values + connection context.
 *
 * This keeps domain-specific URL construction out of the generic field
 * components while still allowing manifests to remain declarative.
 */

import type { ConnectionReference } from '@/types/connectionReference';
import { buildFoundryPortalUrl, type FoundryAgentVersion } from './foundryAgentService';

export interface ExternalLinkContext {
  /** Current values of all parameters in the node. */
  parameterValues: Record<string, unknown>;
  /** Connection reference for the node, if a connection is established. */
  connectionReference?: ConnectionReference | undefined;
  /** Active tenant used to keep Foundry portal navigation in the signed-in context. */
  tenantId?: string | undefined;
}

export type ExternalLinkBuilder = (ctx: ExternalLinkContext) => string | undefined;

const registry = new Map<string, ExternalLinkBuilder>();

export function registerExternalLinkBuilder(id: string, builder: ExternalLinkBuilder): void {
  registry.set(id, builder);
}

export function getExternalLinkBuilder(id: string): ExternalLinkBuilder | undefined {
  return registry.get(id);
}

/** Test-only: clear all registered builders. */
export function __clearExternalLinkBuilderRegistry(): void {
  registry.clear();
}

// =============================================================================
// REGISTERED BUILDERS
// =============================================================================

function readString(values: Record<string, unknown>, key: string): string | undefined {
  const v = values[key];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

function readConnectionProps(
  ref: ConnectionReference | undefined
): { endpoint?: string; resourceId?: string } {
  const props = ref?.connectionProperties;
  if (!props) return {};
  const endpoint = typeof props['endpoint'] === 'string' ? (props['endpoint'] as string) : undefined;
  const resourceId =
    typeof props['resourceId'] === 'string' ? (props['resourceId'] as string) : undefined;
  return {
    ...(endpoint ? { endpoint } : {}),
    ...(resourceId ? { resourceId } : {}),
  };
}

/**
 * Builds a deep-link to the AI Foundry portal for the currently-selected agent.
 * Reads agent name + version from parameterValues and account/project metadata
 * from the connection reference.
 */
registerExternalLinkBuilder('foundryPortalAgent', (ctx) => {
  const agentName =
    readString(ctx.parameterValues, 'foundryAgentName') ??
    readString(ctx.parameterValues, 'foundryAgentId');
  const versionRaw = readString(ctx.parameterValues, 'foundryAgentVersion');
  const version: FoundryAgentVersion = versionRaw === 'v1' ? 'v1' : 'v2';
  const { endpoint, resourceId } = readConnectionProps(ctx.connectionReference);
  return buildFoundryPortalUrl(resourceId, endpoint, agentName, version, ctx.tenantId);
});
